import { fileMap, languages } from '../../constants/languages';
import { keycodesModule } from '../../constants/keycodes';
import { accountServiceModule } from '../../services/account-service/account-service';
import { ucFirst } from '../../utils/string-utils';
import { globalActionsComponentModule } from '../global-actions/global-actions';

let fontNode;

export const CODEMIRROR_DEFAULT_OPTIONS = {
  indentUnit: 2,
  tabSize: 4,
  indentWithTabs: false,
  lineWrapping: true,
  smartIndent: true,
  electricChars: true,
  lineNumbers: true,
  themes: {
    dark: 'qualified-dark',
    light: 'qualified-light',
  },
  theme: 'light',
  keyMap: 'default',
  matchBrackets: true,
  autoCloseBrackets: true,
  foldGutter: true,
  gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
  styleActiveLine: true,
  extraKeys: {},
  font: '',
  fileMap: {},
  undoDepth: 2000,
};

// These properties are inherited by per-editor options, so they point back to the
// shared options
export const CODEMIRROR_EDITABLE_OPTIONS = [
  'indentUnit',
  'tabSize',
  'indentWithTabs',
  'lineWrapping',
  'smartIndent',
  'electricChars',
  'lineNumbers',
  'theme',
  'keyMap',
  'matchBrackets',
  'autoCloseBrackets',
  'foldGutter',
  'styleActiveLine',
  'font',
  'fileMap',
];

export const codemirrorOptionsComponentModule = angular
  .module('Q.components.codemirror-options', [
    keycodesModule,
    accountServiceModule,
    globalActionsComponentModule,
  ])

  .factory(
    'codemirrorOptions',
    function (
      accountService,
      $rootScope,
      $document,
      $window,
      KeyCodes,
      globalActionsService,
      $timeout,
    ) {
      let sharedOpts;
      let _opts = {};

      function _setupOpts() {
        initializeExtraKeys(
          $window.CodeMirror,
          KeyCodes.COMMAND_KEY,
          globalActionsService,
        );
        _opts = angular.merge(
          {},
          CODEMIRROR_DEFAULT_OPTIONS,
          accountService.prefs().codemirror,
        );
        updateFont();
        updateFileMap();

        // setup default theme
        _updateTheme(_opts.theme);
      }

      _setupOpts();
      if (!accountService.get().verified) {
        accountService.whenReady().then(_setupOpts);
      }
      $rootScope.$on('app.login', _setupOpts);
      $rootScope.$on('app.logout', _setupOpts);

      // Shared options are sort of a magic source for handling editable options
      // when values are set here, certain actions are automatically applied to the base options
      sharedOpts = {};
      Object.keys(_opts).forEach((opt) => {
        Object.defineProperty(sharedOpts, opt, {
          get() {
            return _opts[opt];
          },
          set(val) {
            _opts[opt] = val;
            _setOption(opt, val, _opts);
          },
          enumerable: true,
          configurable: true,
        });
      });
      Object.defineProperty(sharedOpts, 'indentSpaces', {
        get() {
          if (sharedOpts.indentWithTabs) return false;
          return sharedOpts.indentUnit;
        },
        set(val) {
          if (val === false) {
            sharedOpts.indentUnit = 4;
            sharedOpts.indentWithTabs = true;
          } else {
            sharedOpts.indentUnit = val;
            sharedOpts.indentWithTabs = false;
          }
        },
      });

      const propUpdateMap = {
        theme: _updateTheme,
        font: updateFont,
        fileMap: updateFileMap,
      };

      function _setOption(option, value, opt) {
        opt[option] = value;

        if (propUpdateMap[option]) {
          propUpdateMap[option](value);
        }

        _savePref(option, value);
      }

      function _updateTheme(value) {
        const $html = $document.find('html');
        angular.forEach(_opts.themes, (_, t) => {
          $rootScope[`$ide${ucFirst(t)}`] = t === value;
          $html.toggleClass('ide-' + t, t === value);
        });
        $rootScope.$ideDarkClass = value === 'dark' ? 'dark-bg' : '';
      }

      let debounceSave;

      function _savePref(option, value) {
        if (CODEMIRROR_EDITABLE_OPTIONS.includes(option)) {
          // save back to user prefs
          const userPrefs = accountService.prefs();
          userPrefs.codemirror = userPrefs.codemirror || {};
          if (angular.equals(value, CODEMIRROR_DEFAULT_OPTIONS[option])) {
            delete userPrefs.codemirror[option];
          } else {
            userPrefs.codemirror[option] = value;
          }
          if (!debounceSave) {
            debounceSave = $timeout(() => {
              if (!$rootScope.playbackToken) accountService.savePrefs();
              $rootScope.$broadcast('codemirrorOptionsChanged', sharedOpts);
              debounceSave = null;
            });
          }
        }
      }

      /**
       * Creates a new set of options based on a given file name (with extension) file type.
       * This is a special object that will inherit editable options (so user preferences are propagated).
       *
       * This returns a unique object every time it is called.
       *
       * @param {string} filename
       * @returns {Object}
       */
      function createOpts(filename) {
        const lang = languages.langFromFilename(filename) || languages.text;
        const mode = lang.codemirror;

        const opts = { mode };
        Object.keys(_opts).forEach((option) => {
          if (option === 'theme') {
            Object.defineProperty(opts, option, {
              get() {
                return _opts.themes[sharedOpts.theme] || _opts.themes.light;
              },
              enumerable: true,
            });
          } else if (CODEMIRROR_EDITABLE_OPTIONS.includes(option)) {
            let _customValue;
            Object.defineProperty(opts, option, {
              get() {
                return _customValue === undefined
                  ? _opts[option]
                  : _customValue;
              },
              set(val) {
                _customValue = val;
              },
              enumerable: true,
            });
          } else {
            opts[option] = angular.copy(_opts[option]);
          }
        });
        if (mode === 'markdown' || mode === 'gfm') {
          opts.extraKeys.Enter = 'newlineAndIndentContinueMarkdownList';
          opts.inputStyle = 'contenteditable';
          opts.spellcheck = true;
        }
        if (lang.ac) {
          opts.extraKeys['Ctrl-Space'] = 'autocomplete';
        }

        return opts;
      }

      /**
       * Trigger an update when the font is modified.
       */
      function updateFont() {
        const font = _opts.font;
        if (font) {
          if (!fontNode) {
            fontNode = angular.element('<style>');
            $document.find('body').append(fontNode);
          }
          fontNode.text(`\
ui-codemirror:not(.markdown-codemirror) pre {\
font: ${font} !important;\
}`);
        } else if (fontNode) {
          fontNode.remove();
          fontNode = null;
        }
      }

      /**
       * Trigger an update with the file map is modified
       */
      function updateFileMap() {
        Object.keys(fileMap).forEach((k) => {
          delete fileMap[k];
        });
        angular.extend(fileMap, _opts.fileMap);
      }

      return {
        sharedOpts,
        createOpts,
        updateFont,
        updateFileMap,
      };
    },
  );

let didInitializeExtraKeys = false;

// Finish setting up the advanced keystrokes
function initializeExtraKeys(CodeMirror, commandKey, globalActionsService) {
  if (!CodeMirror || didInitializeExtraKeys) return;
  // from the sublime keymap
  let cmds = CodeMirror.commands;
  let map = CodeMirror.keyMap['default'];
  let Pos = CodeMirror.Pos;

  map['Tab'] = 'indentMore';
  map['Shift-Tab'] = 'indentLess';
  map[commandKey + '--'] = 'toggleFold';
  map[commandKey + '-/'] = 'toggleComment';
  // enable global apply action for Vim
  cmds.save = globalActionsService.apply;

  cmds[(map[commandKey + '-D'] = 'duplicateLine')] = function (cm) {
    cm.operation(function () {
      const rangeCount = cm.listSelections().length;
      for (let i = 0; i < rangeCount; i++) {
        const range = cm.listSelections()[i];
        if (range.empty()) {
          cm.replaceRange(
            cm.getLine(range.head.line) + '\n',
            Pos(range.head.line, 0),
          );
        } else {
          cm.replaceRange(cm.getRange(range.from(), range.to()), range.from());
        }
      }
      cm.scrollIntoView();
    });
  };

  const swapLineCombo = 'Shift-' + commandKey + '-';

  cmds[(map['Shift-Alt-Up'] = map[swapLineCombo + 'Up'] = 'swapLineUp')] =
    function swapLineUp(cm) {
      if (cm.isReadOnly()) return CodeMirror.Pass;
      const ranges = cm.listSelections(),
        linesToMove = [];
      let at = cm.firstLine() - 1;
      const newSels = [];
      for (let i = 0; i < ranges.length; i++) {
        const range = ranges[i];
        let from = range.from().line - 1;
        let to = range.to().line;
        newSels.push({
          anchor: Pos(range.anchor.line - 1, range.anchor.ch),
          head: Pos(range.head.line - 1, range.head.ch),
        });
        if (range.to().ch === 0 && !range.empty()) --to;
        if (from > at) {
          linesToMove.push(from, to);
        } else if (linesToMove.length) linesToMove[linesToMove.length - 1] = to;
        at = to;
      }
      cm.operation(function () {
        for (let i = 0; i < linesToMove.length; i += 2) {
          const from = linesToMove[i];
          const to = linesToMove[i + 1];
          const line = cm.getLine(from);
          cm.replaceRange('', Pos(from, 0), Pos(from + 1, 0), '+swapLine');
          if (to > cm.lastLine()) {
            cm.replaceRange('\n' + line, Pos(cm.lastLine()), null, '+swapLine');
          } else {
            cm.replaceRange(line + '\n', Pos(to, 0), null, '+swapLine');
          }
        }
        cm.setSelections(newSels);
        cm.scrollIntoView();
      });
    };

  cmds[(map['Shift-Alt-Down'] = map[swapLineCombo + 'Down'] = 'swapLineDown')] =
    function swapLineDown(cm) {
      if (cm.isReadOnly()) return CodeMirror.Pass;
      const ranges = cm.listSelections(),
        linesToMove = [];
      let at = cm.lastLine() + 1;
      for (let i = ranges.length - 1; i >= 0; i--) {
        const range = ranges[i];
        let from = range.to().line + 1;
        let to = range.from().line;
        if (range.to().ch === 0 && !range.empty()) from--;
        if (from < at) {
          linesToMove.push(from, to);
        } else if (linesToMove.length) linesToMove[linesToMove.length - 1] = to;
        at = to;
      }
      cm.operation(function () {
        for (let i = linesToMove.length - 2; i >= 0; i -= 2) {
          const from = linesToMove[i];
          const to = linesToMove[i + 1];
          const line = cm.getLine(from);
          if (from === cm.lastLine()) {
            cm.replaceRange('', Pos(from - 1), Pos(from), '+swapLine');
          } else {
            cm.replaceRange('', Pos(from, 0), Pos(from + 1, 0), '+swapLine');
          }
          cm.replaceRange(line + '\n', Pos(to, 0), null, '+swapLine');
        }
        cm.scrollIntoView();
      });
    };

  didInitializeExtraKeys = true;
}

export function cursorData(cm, base = {}) {
  const cursor = cm.getCursor();
  base.line = cursor.line;
  base.ch = cursor.ch;
  return base;
}
